// The session journal's rules (SPEC F10 "session journal", base in v0.6), pure: a state, an event and the clock
// in, the next state and the writes out. The store wires it to the real clock, timers and visibility events;
// tests drive it with a fake one.
//
// A session starts on the first activity in a project and ends after 20 min idle, when you leave the project,
// or when the app goes to the background (the iOS rule, §10). Going to the background ends it, but coming back
// within 5 min and carrying on in the same project continues the same session (DECISIONS 2026-10-08).
// A row's `endedAt` is always the time of the last activity, so an idle end needs no write and an idle session
// is not credited with the 20 minutes of waiting.

import type { Session } from '../data/types';

export const IDLE_MS = 20 * 60 * 1000;
export const BACKGROUND_GRACE_MS = 5 * 60 * 1000;

export interface OpenSession {
  id: string;
  projectId: string;
  startedAt: number;
  lastActivityAt: number;
}

export interface JournalState {
  /** The session you are in. */
  open?: OpenSession;
  /** A session the app going to the background ended, still continuable for the grace period. */
  hidden?: { session: OpenSession; at: number };
}

export interface Activity {
  /** A step that was checked (not unchecked). */
  stepId?: string;
  /** The phase the activity belongs to. */
  phaseId?: string;
}

export type JournalEvent =
  | { type: 'activity'; projectId: string; activity?: Activity }
  | { type: 'leave' }
  | { type: 'hide' }
  | { type: 'show' }
  | { type: 'tick' };

export type JournalWrite =
  | { op: 'start'; session: Session }
  | { op: 'extend'; id: string; endedAt: number; stepId?: string; phaseId?: string };

export interface JournalResult {
  state: JournalState;
  writes: JournalWrite[];
  /** A session that has just ended for good (so a check-out can be offered for it). */
  ended?: string;
}

export const emptyJournal: JournalState = {};

const idle = (s: OpenSession, now: number): boolean => now - s.lastActivityAt >= IDLE_MS;

/** `newId` is only called when a session starts. */
export function step(state: JournalState, event: JournalEvent, now: number, newId: () => string): JournalResult {
  switch (event.type) {
    case 'activity': {
      const { projectId, activity } = event;
      let open = state.open;
      let ended: string | undefined;

      // The grace period only holds across a background; any activity after it ends the old session for good
      const hidden = state.hidden;
      if (!open && hidden) {
        if (hidden.session.projectId === projectId && now - hidden.at <= BACKGROUND_GRACE_MS) open = hidden.session;
        else ended = hidden.session.id;
      }
      // Another project, or too long since the last activity: that session is over, a new one starts
      if (open && (open.projectId !== projectId || idle(open, now))) {
        ended = open.id;
        open = undefined;
      }

      const writes: JournalWrite[] = [];
      if (!open) {
        const session: Session = {
          id: newId(), projectId, startedAt: now, endedAt: now,
          stepIds: activity?.stepId ? [activity.stepId] : [],
          phaseIds: activity?.phaseId ? [activity.phaseId] : [],
        };
        writes.push({ op: 'start', session });
        open = { id: session.id, projectId, startedAt: now, lastActivityAt: now };
      } else {
        open = { ...open, lastActivityAt: now };
        writes.push({ op: 'extend', id: open.id, endedAt: now, ...(activity?.stepId ? { stepId: activity.stepId } : {}), ...(activity?.phaseId ? { phaseId: activity.phaseId } : {}) });
      }
      return { state: { open }, writes, ...(ended ? { ended } : {}) };
    }

    case 'leave': {
      const ended = state.open?.id ?? state.hidden?.session.id;
      return { state: {}, writes: [], ...(ended ? { ended } : {}) };
    }

    case 'hide':
      // Not ended yet: the next show decides whether it was a quick switch or a break
      return { state: state.open ? { hidden: { session: state.open, at: now } } : state, writes: [] };

    case 'show': {
      const hidden = state.hidden;
      if (!hidden) return { state, writes: [] };
      if (now - hidden.at <= BACKGROUND_GRACE_MS) return { state: { open: hidden.session }, writes: [] };
      return { state: {}, writes: [], ended: hidden.session.id };
    }

    case 'tick': {
      if (state.open && idle(state.open, now)) return { state: {}, writes: [], ended: state.open.id };
      return { state, writes: [] };
    }
  }
}

/** Apply a write to a session row (the store and the repo share this, so tests can check it without Dexie). */
export function applyWrite(rows: Session[], write: JournalWrite): Session[] {
  if (write.op === 'start') return [...rows, write.session];
  return rows.map((s) =>
    s.id !== write.id
      ? s
      : {
          ...s,
          endedAt: write.endedAt,
          stepIds: write.stepId && !s.stepIds.includes(write.stepId) ? [...s.stepIds, write.stepId] : s.stepIds,
          phaseIds: write.phaseId && !s.phaseIds.includes(write.phaseId) ? [...s.phaseIds, write.phaseId] : s.phaseIds,
        },
  );
}

/**
 * The session to offer a check-out for (SPEC F10): the newest one that has ended without an outcome, that this
 * run is not in the middle of, and that has not been offered yet (`seen`). Older unanswered ones are never asked,
 * so a long break never turns into a queue of sheets.
 */
export function checkoutCandidate(
  sessions: readonly Session[],
  opts: { seen?: string; current?: readonly string[]; alive: (projectId: string) => boolean },
): Session | undefined {
  const latest = [...sessions].filter((s) => !(opts.current ?? []).includes(s.id)).sort((a, b) => b.startedAt - a.startedAt)[0];
  if (!latest || latest.outcome !== undefined || latest.id === opts.seen || !opts.alive(latest.projectId)) return undefined;
  return latest;
}
